import React, { ReactNode, useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useSnackbar } from 'notistack';
import { getUsers, deleteUser, deactivateUser, enableUser } from 'src/api/userManagement';
import { PAGE_INFO } from 'src/util/constants';
import { FmtpError, FmtpErrorCode, UserManagementSortingOptions, UserManagementUser } from '@lanterds/types';
import { FmtpPage } from 'src/components/common/NavigationMenu/types';
import './ManageUsers.scss';
import { useFmtpNavigate } from 'src/hooks/useFmtpNavigate';
import { getUserEmailFromToken } from 'src/util/helpers';
import { isAuthorized } from 'src/util/Permissions';
import { Permissions } from '@lanterds/types';
import { HardcodedPermissionDisplayNames } from 'src/util/constants';
import { isObject } from 'lodash';
import CreateUserDialog from './CreateUserDialog';
import { PageNames } from 'src/util/types';

type UserManagementColumns = {
    email: string;
    firstName: string;
    lastName: string;
    enabled: boolean;
};

export default function ManageUsers() {
    const fmtpNavigate = useFmtpNavigate();

    const [selectedRows, setSelectedRows] = useState<{ [rowId: string]: boolean }>({});
    const [usersData, setUsersData] = useState<UserManagementUser[] | null>(null);
    const [allUsersData, setAllUsersData] = useState<UserManagementUser[] | null>(null);
    const { data, isFetching, refetch } = getUsers();
    const { mutateAsync: deleteSelectedUser, isPending: isPendingDelete } = deleteUser();
    const { mutateAsync: deactivateSelectedUser, isPending: isPendingDeactivation } = deactivateUser();
    const { mutateAsync: enableSelectedUser, isPending: isPendingEnable } = enableUser();
    const [showDisabled, setShowDisabled] = useState(false);

    const [userForDelete, setUserForDelete] = useState<UserManagementUser | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const [usersForDeactivate, setUsersForDeactivate] = useState<UserManagementUser[]>([]);
    const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);

    const [usersForEnable, setUsersForEnable] = useState<UserManagementUser[]>([]);
    const [enableModalOpen, setEnableModalOpen] = useState(false);

    const [alertModalOpen, setAlertModalOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);

    const loggedInUserEmail = getUserEmailFromToken();

    const handleOpenCreateUserDialog = () => {
        setCreateUserDialogOpen(true);
    };

    const handleCloseCreateUserDialog = () => {
        setCreateUserDialogOpen(false);
    };

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    useEffect(() => {
        closeSnackbar();
        const key = enqueueSnackbar('Fetching users...', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        if (data) {
            closeSnackbar(key);
            setAllUsersData(
                data
                    .sort((a: UserManagementUser, b: UserManagementUser) =>
                        a.email.toLocaleLowerCase().localeCompare(b.email.toLocaleLowerCase()),
                    )
                    .map((user: any) => {
                        user.tooltip = createTooltipTitle(user);
                        return user;
                    }),
            );
        }
    }, [data, isFetching]);

    const filteredUsers = useMemo(() => {
        if (usersData) return showDisabled ? usersData : usersData.filter((user) => user.enabled === true);
        return null;
    }, [usersData, showDisabled]);

    useEffect(() => {
        if (allUsersData) {
            setUsersData(
                allUsersData.sort((a: UserManagementUser, b: UserManagementUser) =>
                    a.email.toLocaleLowerCase().localeCompare(b.email.toLocaleLowerCase()),
                ),
            );
        }
    }, [allUsersData]);

    const onUserClick = (user: UserManagementColumns) => {
        const userPage = PAGE_INFO.admin.subPages?.find((page: FmtpPage) => page.name === PageNames.User);
        if (!userPage) {
            const message = 'An error occurred while navigating to the user profile page.';
            const errorCode: FmtpErrorCode = 'SHU020';
            throw new FmtpError({
                message,
                errorCode,
            });
        }

        fmtpNavigate(`${userPage.href}/${user.email}`);
    };

    const handleUserCreated = async () => {
        await refetch();
    };

    const selectedKeys = Object.keys(selectedRows);
    const selectedUsers = filteredUsers ? selectedKeys.map((key) => filteredUsers[Number(key)]) : [];

    const isEnableButtonEnabled = showDisabled && selectedUsers.length > 0 && selectedUsers.some((user) => !user.enabled);
    const isDeactivateButtonEnabled = selectedUsers.length > 0 && selectedUsers.some((user) => user.enabled);

    const handleDeactivateUserClick = async () => {
        if (selectedKeys.length < 1) {
            setAlertMessage('Please select at least one user before Deactivating.');
            setAlertModalOpen(true);
            return;
        } else {
            const activeUsers = selectedUsers.filter((user) => user.enabled);
            if (activeUsers.length < 1) {
                setAlertMessage('None of the selected users are active.');
                setAlertModalOpen(true);
                return;
            }
            setUsersForDeactivate(activeUsers);
            setDeactivateModalOpen(true);
        }
    };

    const handleConfirmDeactivate = async () => {
        await Promise.all(
            usersForDeactivate.map(async (user) => {
                if (user.email) {
                    await deactivateSelectedUser({ userId: user.email });
                }
            }),
        );
        const deactivatedEmails = usersForDeactivate.map((user) => user.email);
        if (usersData)
            setUsersData(usersData.filter((dataUser: UserManagementUser) => !deactivatedEmails.includes(dataUser.email)));
        setSelectedRows({});
        setDeactivateModalOpen(false);
        setUsersForDeactivate([]);
        await refetch();
    };

    const handleDeleteUserClick = () => {
        if (selectedKeys.length < 1) {
            setAlertMessage('Please select a user before Deleting.');
            setAlertModalOpen(true);
            return;
        } else if (selectedKeys.length > 1) {
            setAlertMessage('Please only select a single user before Deleting.');
            setAlertModalOpen(true);
            return;
        } else {
            const user = filteredUsers ? filteredUsers[Number(selectedKeys[0])] : null;

            if (user?.email) {
                setUserForDelete(user);
                setDeleteModalOpen(true);
            }
        }
    };

    const handleConfirmDelete = async () => {
        if (userForDelete?.email) {
            await deleteSelectedUser({ userId: userForDelete.email });
            setSelectedRows({});
            if (usersData)
                setUsersData(usersData.filter((dataUser: UserManagementUser) => dataUser.email !== userForDelete.email));
            setDeleteModalOpen(false);
            setUserForDelete(null);
            await refetch();
        }
    };

    const handleEnableUserClick = async () => {
        if (selectedKeys.length < 1) {
            setAlertMessage('Please select at least one user before Enabling.');
            setAlertModalOpen(true);
            return;
        } else {
            const disabledUsers = selectedUsers.filter((user) => !user.enabled);
            if (disabledUsers.length < 1) {
                setAlertMessage('None of the selected users are disabled.');
                setAlertModalOpen(true);
                return;
            }
            setUsersForEnable(disabledUsers);
            setEnableModalOpen(true);
        }
    };

    const handleConfirmEnable = async () => {
        await Promise.all(
            usersForEnable.map(async (user) => {
                if (user.email) {
                    await enableSelectedUser({ userId: user.email });
                }
            }),
        );
        const enabledEmails = usersForEnable.map((user) => user.email);
        if (allUsersData)
            setAllUsersData(
                allUsersData.map((dataUser: UserManagementUser) => {
                    if (enabledEmails.includes(dataUser.email)) {
                        dataUser.enabled = true;
                    }
                    return dataUser;
                }),
            );
        setSelectedRows({});
        setEnableModalOpen(false);
        setUsersForEnable([]);
        await refetch();
    };

    const createTooltipTitle = (user: any) => {
        const notFound = 'Not Found';
        const modifiedBy = user.modifiedBy ? `Modified By: ${user.modifiedBy}` : null;
        const modifiedOn = user.modifiedOn ? `Modified On: ${user.modifiedOn}` : null;
        const createdBy = `Created By: ${user.createdBy || notFound}`;
        const createdOn = `Created On: ${user.createdOn || notFound}`;
        const permissionKeys = isObject(user.permissions) ? Object.keys(user.permissions) : [];
        const permissionsDisplays = permissionKeys.map((permissionName) => {
            return HardcodedPermissionDisplayNames[permissionName as Permissions];
        });

        return (
            <div>
                {createdBy}
                <br />
                {createdOn}
                <br />
                {modifiedBy ? (
                    <div>
                        {modifiedBy}
                        <br />
                        {modifiedOn}
                        <br />
                    </div>
                ) : null}
                Permissions:
                <br />
                {permissionsDisplays.join(', ')}
            </div>
        );
    };

    useEffect(() => {
        const keys = Object.keys(selectedRows);
        if (keys.length === 0) return;

        const selected = usersData ? keys.map((key) => usersData[Number(key)]).filter(Boolean) : [];
        const allEnabled = selected.every((user) => user.enabled);
        const allDisabled = selected.every((user) => !user.enabled);

        if (!allEnabled && !allDisabled) {
            const preferredType = selected[0].enabled;
            setSelectedRows((prevSelected) => {
                const newSelection: Record<string, boolean> = {};
                keys.forEach((key) => {
                    const user = usersData ? usersData[Number(key)] : null;
                    if (user && user.enabled === preferredType) {
                        newSelection[key] = true;
                    }
                });
                return newSelection;
            });
        }
    }, [selectedRows, usersData]);

    const columns: ColumnDef<UserManagementColumns>[] = useMemo(() => {
        const baseColumns: ColumnDef<UserManagementColumns>[] = [
            {
                id: 'select',
                size: 10,
                enableColumnFilter: false,
            },
            {
                accessorKey: 'email',
                header: 'Email',
                sortingFn: 'alphanumeric',
                meta: {
                    sortingOptionName: UserManagementSortingOptions.email,
                    innerHtml: ({ row, getValue }: CellContext<UserManagementColumns, unknown>) => {
                        const { email } = row.original;
                        return (
                            <Typography onClick={() => onUserClick(row.original)} className="email-cell-text">
                                {getValue() as ReactNode}
                            </Typography>
                        );
                    },
                },
            },
            {
                accessorKey: 'firstName',
                sortingFn: 'alphanumeric',
                header: 'First Name',
            },
            {
                accessorKey: 'lastName',
                sortingFn: 'alphanumeric',
                header: 'Last Name',
            },
        ];
        if (showDisabled) {
            baseColumns.push({
                accessorKey: 'enabled',
                size: 30,
                sortingFn: 'auto',
                header: 'Disabled',
                meta: {
                    innerHtml: ({ getValue }: CellContext<UserManagementColumns, unknown>) => {
                        return !getValue() ? 'Yes' : '';
                    },
                },
            });
        }
        return baseColumns;
    }, [showDisabled]);

    return (
        <Box id="manage-users-container" className="auto-fill-height">
            <Card id="manage-users-card" className="auto-fill-height">
                <Grid container spacing={2}>
                    <Grid xs={12} className="manage-users-header">
                        <Typography variant="h5">Users</Typography>
                    </Grid>
                    <Grid xs={12} container className="user-buttons-grid">
                        <Grid>
                            <Button variant="contained" size="medium" onClick={handleOpenCreateUserDialog}>
                                Create New User
                            </Button>
                        </Grid>
                        <Grid>
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={handleDeactivateUserClick}
                                disabled={!isDeactivateButtonEnabled}
                            >
                                Deactivate User
                            </Button>
                        </Grid>
                        <Grid>
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={handleEnableUserClick}
                                disabled={!isEnableButtonEnabled}
                            >
                                Enable User
                            </Button>
                        </Grid>
                        {isAuthorized(loggedInUserEmail, Permissions.userManagement_deleteUser) && (
                            <Grid>
                                <Button variant="contained" size="medium" onClick={handleDeleteUserClick}>
                                    Delete User
                                </Button>
                            </Grid>
                        )}
                        <Grid>
                            <FormControlLabel
                                label="Show Disabled Users"
                                control={
                                    <Checkbox
                                        checked={showDisabled}
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setShowDisabled(isChecked);
                                            if (!isChecked) {
                                                setSelectedRows((prevSelected) => {
                                                    const updated: Record<string, boolean> = {};
                                                    Object.keys(prevSelected).forEach((key) => {
                                                        const user = usersData ? usersData[Number(key)] : null;
                                                        if (user && user.enabled) {
                                                            updated[key] = true;
                                                        }
                                                    });
                                                    return updated;
                                                });
                                            }
                                        }}
                                    />
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <FmtpTable
                    data={filteredUsers ?? []}
                    columns={columns}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                    enableSorting={true}
                    isResizable={true}
                    enableFilters={true}
                    isFetching={isFetching || isPendingDeactivation || isPendingDelete || isPendingEnable || !filteredUsers}
                    disableSelectAllHeader={true}
                    showTable={!isFetching && !!filteredUsers}
                />
            </Card>

            <Dialog open={alertModalOpen} onClose={() => setAlertModalOpen(false)}>
                <DialogTitle>Alert</DialogTitle>
                <DialogContent>
                    <DialogContentText>{alertMessage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setAlertModalOpen(false)}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deactivateModalOpen} onClose={() => setDeactivateModalOpen(false)}>
                <DialogTitle>Deactivate User(s)</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to deactivate the following users:{' '}
                        {usersForDeactivate.map((user) => user.email).join(', ')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setDeactivateModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleConfirmDeactivate} color="warning">
                        Deactivate
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
                <DialogTitle>Delete User</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete {userForDelete?.email}?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleConfirmDelete} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={enableModalOpen} onClose={() => setEnableModalOpen(false)}>
                <DialogTitle>Enable User(s)</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to enable the following users:{' '}
                        {usersForEnable.map((user) => user.email).join(', ')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setEnableModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleConfirmEnable} color="primary">
                        Enable
                    </Button>
                </DialogActions>
            </Dialog>
            <CreateUserDialog
                open={createUserDialogOpen}
                onClose={handleCloseCreateUserDialog}
                onUserCreated={handleUserCreated}
            />
        </Box>
    );
}
